import { Ship, Plane, Truck, AlertTriangle, CheckCircle2, Info, Users } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { QuoteOption, QuotedLeg } from '../types/trade.types';
import { formatNumericDate } from '../utils/trade-dates';
import { resumenPorActor } from '../utils/actor-tarifa';

/**
 * Detalle de la opción elegida: de dónde sale cada dólar.
 *
 * Se muestra una sola vez, debajo de la comparación, en vez de repetirla en
 * cada columna. La comparación sirve para decidir; esto para poder explicarlo
 * al cliente, que es lo que Delicia pedía: "nunca un total sin desglose".
 */

const MODE_ICON: Record<string, typeof Ship> = { SEA: Ship, AIR: Plane, LAND: Truck };

function money(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

const shortDate = (iso: string | null) => formatNumericDate(iso, 'sin vencimiento');

/**
 * ¿La tarifa es real o de relleno? El tarifario marca cada una con VERIFICADO
 * o SIMULADO; es lo que separa lo que se puede prometer de lo que no.
 */
function sourceLabel(source: string | null): { text: string; tone: 'neutral' | 'warn' } | null {
  if (!source) return null;
  if (source.includes('VERIFICADO')) return { text: 'tarifa del tarifario', tone: 'neutral' };
  if (source.includes('SIMULADO')) return { text: 'dato simulado', tone: 'warn' };
  return null;
}

export interface QuoteOptionDetailProps {
  option: QuoteOption;
}

export function QuoteOptionDetail({ option }: QuoteOptionDetailProps) {
  // El desglose por ACTOR (naviera, aduana, seguro, liberación…). Pedido de Delicia:
  // que se vea de qué actor sale cada parte del precio, no solo el total.
  const porActor = resumenPorActor(option.legs);

  return (
    <section className="rounded-xl border bg-card">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b px-4 py-3">
        <h3 className="font-semibold">
          Desglose · {option.via ? `vía ${option.via}` : 'ruta directa'}
        </h3>
        <p className="font-titulo font-semibold tabular-nums">
          {money(option.totalPrice, option.currency)}
        </p>
      </header>

      {option.warnings.length > 0 && (
        <div className="space-y-1 border-b bg-alerta-suave px-4 py-2">
          {option.warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-1.5 text-xs leading-snug text-alerta">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {w}
            </p>
          ))}
        </div>
      )}

      <div className="space-y-5 p-4">
        {/* ── Resumen por actor ── (pedido de Delicia)
            La cotización es la suma de lo que cobra cada jugador de la cadena.
            Acá se ve agrupado: naviera, transportista, agencia aduanera, seguro,
            liberación de puerto, etc. — antes del detalle tramo por tramo. */}
        {porActor.length > 1 && (
          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <Users className="h-3.5 w-3.5" aria-hidden="true" />
              Por actor
            </p>
            {/* Con su barra (artefacto): cuánto pesa cada uno en el precio, sin leer los números. */}
            <ul className="space-y-2">
              {porActor.map((a) => {
                const peso = option.totalPrice > 0 ? (a.total / option.totalPrice) * 100 : 0;
                return (
                  <li
                    key={a.actor}
                    className="grid grid-cols-[minmax(0,10rem)_1fr_auto_3rem] items-center gap-3 text-sm"
                  >
                    <span className="truncate text-foreground">{a.label}</span>
                    <span className="h-2 rounded-full bg-muted" aria-hidden="true">
                      <span
                        className="block h-full rounded-full bg-primary"
                        style={{ width: `${Math.min(100, peso)}%` }}
                      />
                    </span>
                    <span className="font-semibold tabular-nums">
                      {money(a.total, option.currency)}
                    </span>
                    <span className="text-right text-xs tabular-nums text-muted-foreground">
                      {Math.round(peso)} %
                    </span>
                  </li>
                );
              })}
            </ul>
            {option.totalMargin > 0 && (
              <div className="mt-2 flex items-baseline justify-between border-t pt-1.5 text-[11px] text-muted-foreground">
                <span>+ margen</span>
                <span className="tabular-nums">{money(option.totalMargin, option.currency)}</span>
              </div>
            )}
            <div className="mt-1 flex items-baseline justify-between text-xs font-semibold">
              <span>Precio total</span>
              <span className="tabular-nums">{money(option.totalPrice, option.currency)}</span>
            </div>
          </div>
        )}

        <ol className="space-y-5">
          {option.legs.map((leg, i) => (
            <LegDetail key={i} leg={leg} isLast={i === option.legs.length - 1} />
          ))}
        </ol>

        {option.landAlternatives.length > 0 && (
          <div className="rounded-lg bg-muted/40 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
              <Users className="h-3 w-3" />
              Otros transportistas para el tramo terrestre
            </p>
            <ul className="space-y-1">
              {option.landAlternatives.map((alt, i) => (
                <li key={i} className="flex items-baseline justify-between gap-3 text-[11px]">
                  <span className={cn('min-w-0', alt.excludedReason && 'text-muted-foreground')}>
                    <span className={cn(alt.excludedReason && 'line-through')}>
                      {alt.providerName}
                    </span>
                    {alt.excludedReason && <span className="ml-1.5">— {alt.excludedReason}</span>}
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {alt.costAmount.toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function LegDetail({ leg, isLast }: { leg: QuotedLeg; isLast: boolean }) {
  const Icon = MODE_ICON[leg.transportMode] ?? Truck;
  const source = sourceLabel(leg.source);

  return (
    <li className="relative pl-7">
      {!isLast && (
        <span className="absolute left-[9px] top-6 bottom--4 w-px bg-border" aria-hidden />
      )}
      <span
        className={cn(
          'absolute left-0 top-0.5 flex h-[19px] w-[19px] items-center justify-center rounded-full',
          'bg-primary text-primary-foreground'
        )}
        aria-hidden
      >
        <Icon className="h-2.5 w-2.5" />
      </span>

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium leading-tight">
          {leg.origin} <span className="text-prixia-silver">→</span> {leg.destination}
        </p>
        <p className="font-mono text-sm font-semibold tabular-nums">
          {money(leg.salePrice, leg.currency)}
        </p>
      </div>

      <p className="mt-0.5 text-xs text-muted-foreground">
        {leg.providerName}
        {leg.agentName && <> · agente {leg.agentName}</>}
        {leg.estimatedDays !== null && <> · {leg.estimatedDays} días</>}
      </p>

      {/* El margen se muestra explícito: el tarifario da costo, no precio. */}
      {leg.marginAmount > 0 && (
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          costo <span className="tabular-nums">{leg.costAmount.toFixed(2)}</span>
          {' + margen '}
          {leg.marginPercent} %{' '}
          <span className="tabular-nums text-prixia-blue">{leg.marginAmount.toFixed(2)}</span>
        </p>
      )}

      {/* La cascada del tarifario, concepto por concepto */}
      {leg.components.length > 1 && (
        <table className="mt-2 w-full max-w-md text-[11px]">
          <tbody>
            {leg.components.map((c, i) => (
              <tr
                key={i}
                className={cn(
                  'text-muted-foreground',
                  /*
                    La línea calculada se ve distinta a propósito: no está en el
                    Excel, la dedujo Prixia restando. Mostrarla igual que un dato
                    importado haría que el vendedor la busque en el tarifario y no
                    la encuentre.
                  */
                  c.isComputed && 'border-t border-dashed border-alerta-borde text-alerta'
                )}
              >
                <td className="py-0.5 pr-3">
                  {c.isComputed && (
                    <span
                      className="mr-1 cursor-help text-[10px]"
                      title="Calculado por Prixia: el total del tarifario no coincide con la suma de sus conceptos. No es una línea del Excel."
                    >
                      ƒ
                    </span>
                  )}
                  {c.concept}
                </td>
                <td className="w-20 py-0.5 text-right tabular-nums">{c.amount.toFixed(2)}</td>
                <td className="w-16 py-0.5 pl-2 text-right text-[10px]">
                  {c.isComputed ? 'calculado' : c.payableAt === 'LOCAL' ? 'local' : 'exterior'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
            leg.isExpired ? 'bg-bloqueo-suave text-bloqueo' : 'bg-ok-suave text-ok'
          )}
        >
          {leg.isExpired ? (
            <AlertTriangle className="h-2.5 w-2.5" />
          ) : (
            <CheckCircle2 className="h-2.5 w-2.5" />
          )}
          {leg.isExpired
            ? `venció ${shortDate(leg.validUntil)}`
            : `vigente hasta ${shortDate(leg.validUntil)}`}
        </span>

        {source && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-medium',
              source.tone === 'warn'
                ? 'bg-alerta-suave text-alerta'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {source.text}
          </span>
        )}
      </div>

      {leg.restrictionNotes && (
        <p className="mt-1 flex items-start gap-1 text-xs text-alerta">
          <Info className="mt-0.5 h-2.5 w-2.5 shrink-0" />
          {leg.restrictionNotes}
        </p>
      )}
    </li>
  );
}
